import { apiService } from './apiService';
import { API_ENDPOINTS } from '../constants';
import { locationUtils } from '../utils/locationUtils';

export const subscriptionService = {
  // Get all subscription plans with billing cycle support
  getPlans: async (billingCycle = 'Monthly') => {
    try {
      // Detect user's country
      const userCountry = await locationUtils.detectUserCountry();
      
      // Add country code and billing cycle as query parameters
      const params = {
        country_code: userCountry.countryCode,
        billing_cycle: billingCycle
      };
      
      const response = await apiService.get(API_ENDPOINTS.SUBSCRIPTIONS.PLANS, { params });
      
      // Transform the API response to match our expected format
      // Backend now returns plan_prices.id as the main id, with subscription_plans.id as plan_id
      const plans = response.data.plans || [];
      const transformedPlans = plans.map(plan => ({
        id: plan.id,                    // This is now plan_prices.id (main identifier)
        plan_id: plan.plan_id,          // This is subscription_plans.id (for reference)
        plan_name: plan.plan_name,
        base_price: parseFloat(plan.price) || 0,
        user_limit: plan.user_limit,
        ai_tokens_limit: plan.ai_tokens_limit,
        features: plan.features ? (Array.isArray(plan.features) ? plan.features : JSON.parse(plan.features)) : [],
        description: plan.description,
        currency: plan.currency,
        currency_symbol: plan.currency_symbol,
        billing_cycle: plan.billing_cycle || billingCycle,
        isactive: plan.isactive
      }));

      return {
        success: true,
        data: transformedPlans,
        billing_cycle: billingCycle,
        currency: response.data.currency
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message,
        data: []
      };
    }
  },

  // Get specific plan by ID with billing cycle support
  getPlanById: async (planId, billingCycle = 'Monthly') => {
    try {
      // Detect user's country
      const userCountry = await locationUtils.detectUserCountry();
      
      // Add country code and billing cycle as query parameters
      const params = {
        country_code: userCountry.countryCode,
        billing_cycle: billingCycle
      };
      
      const response = await apiService.get(`${API_ENDPOINTS.SUBSCRIPTIONS.PLANS}/${planId}`, { params });
      const plan = response.data.data;
      
      // Transform the plan to match our expected format
      // Backend now returns plan_prices.id as the main id, with subscription_plans.id as plan_id
      const transformedPlan = {
        id: plan.id,                    // This is now plan_prices.id (main identifier)
        plan_id: plan.plan_id,          // This is subscription_plans.id (for reference)
        plan_name: plan.plan_name,
        base_price: parseFloat(plan.price) || 0,
        user_limit: plan.user_limit,
        ai_tokens_limit: plan.ai_tokens_limit,
        features: plan.features ? (Array.isArray(plan.features) ? plan.features : JSON.parse(plan.features)) : [],
        description: plan.description,
        currency: plan.currency,
        currency_symbol: plan.currency_symbol,
        billing_cycle: plan.billing_cycle || billingCycle,
        isactive: plan.isactive
      };

      return {
        success: true,
        data: transformedPlan
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Assign subscription to client
  assign: async (subscriptionData) => {
    try {
      const response = await apiService.post(API_ENDPOINTS.SUBSCRIPTIONS.ASSIGN, subscriptionData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Get client's subscription
  getClientSubscription: async (clientId) => {
    try {
      const response = await apiService.get(`${API_ENDPOINTS.SUBSCRIPTIONS.CLIENT_SUBSCRIPTION}/${clientId}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Validate user limit
  validateUserLimit: async (clientId, productId) => {
    try {
      const response = await apiService.get(API_ENDPOINTS.SUBSCRIPTIONS.VALIDATE_USER_LIMIT, {
        params: { client_id: clientId, product_id: productId }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Validate token limit
  validateTokenLimit: async (clientId, requestedTokens = 0) => {
    try {
      const response = await apiService.get(API_ENDPOINTS.SUBSCRIPTIONS.VALIDATE_TOKEN_LIMIT, {
        params: { client_id: clientId, requested_tokens: requestedTokens }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Get plans with specific country code and billing cycle (for testing/manual override)
  getPlansByCountry: async (countryCode, billingCycle = 'Monthly') => {
    try {
      const params = {
        country_code: countryCode,
        billing_cycle: billingCycle
      };
      
      const response = await apiService.get(API_ENDPOINTS.SUBSCRIPTIONS.PLANS, { params });
      
      // Transform the API response to match our expected format
      // Backend now returns plan_prices.id as the main id, with subscription_plans.id as plan_id
      const plans = response.data.plans || [];
      const transformedPlans = plans.map(plan => ({
        id: plan.id,                    // This is now plan_prices.id (main identifier)
        plan_id: plan.plan_id,          // This is subscription_plans.id (for reference)
        plan_name: plan.plan_name,
        base_price: parseFloat(plan.price) || 0,
        user_limit: plan.user_limit,
        ai_tokens_limit: plan.ai_tokens_limit,
        features: plan.features ? (Array.isArray(plan.features) ? plan.features : JSON.parse(plan.features)) : [],
        description: plan.description,
        currency: plan.currency,
        currency_symbol: plan.currency_symbol,
        billing_cycle: plan.billing_cycle || billingCycle,
        isactive: plan.isactive
      }));

      return {
        success: true,
        data: transformedPlans,
        countryCode,
        billing_cycle: billingCycle,
        currency: response.data.currency
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message,
        data: [],
        countryCode,
        billing_cycle: billingCycle
      };
    }
  }
}; 